import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext.js';
import { Navbar } from './components/Navbar.js';
import { ProtectionStatusBar } from './components/ProtectionStatusBar.js';
import { Footer } from './components/Footer.js';
import { ProtectedRoute } from './components/ProtectedRoute.js';
import { trackConversion } from './services/conversionTracking.js';
import { Landing } from './pages/Landing.js';
import { LandingV1 } from './pages/LandingV1.js';
import { LandingV2 } from './pages/LandingV2.js';
import { Connect } from './pages/Connect.js';
import { SubscriptionSuccess } from './pages/SubscriptionSuccess.js';
import { SubscriptionCancel } from './pages/SubscriptionCancel.js';
import { Support } from './pages/Support.js';
import { RefundPolicy } from './pages/RefundPolicy.js';
import { CancellationPolicy } from './pages/CancellationPolicy.js';
import { BillingPolicy } from './pages/BillingPolicy.js';
import { Terms } from './pages/Terms.js';
import { Privacy } from './pages/Privacy.js';
import { Disclaimer } from './pages/Disclaimer.js';
import { TransferSubscription } from './pages/TransferSubscription.js';
import { RecoverSubscription } from './pages/RecoverSubscription.js';
import { Pricing } from './pages/Pricing.js';
import { TokenIntel } from './pages/TokenIntel.js';
import { IntelDemo } from './pages/IntelDemo.js';
import { ProviderHealth } from './pages/ProviderHealth.js';
import { QaDashboard } from './pages/QaDashboard.js';
import { QaScenarioDetail } from './pages/QaScenarioDetail.js';
import { WebSearch } from './pages/WebSearch.js';
import { Dashboard } from './pages/Dashboard.js';
import { SnapDeepIntelPage } from './pages/SnapDeepIntelPage.js';

// Feature flag: VITE_LANDING_VERSION=v1 (default) or v2
const LANDING_VERSION = import.meta.env.VITE_LANDING_VERSION || 'v1';
const LandingPage = LANDING_VERSION === 'v2' ? LandingV2 : LANDING_VERSION === 'v1' ? LandingV1 : Landing;

function PageTracker() {
  const location = useLocation();
  useEffect(() => {
    trackConversion('page_view', { path: location.pathname });
  }, [location.pathname]);
  return null;
}

function AppContent() {
  const { authLoading } = useAuth();

  return (
    <BrowserRouter>
      <PageTracker />
      <Navbar />
      <ProtectionStatusBar />
      <main>
        {authLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
            <div>Loading...</div>
          </div>
        ) : (
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/connect" element={<Connect />} />
            <Route path="/support" element={<Support />} />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/cancellation-policy" element={<CancellationPolicy />} />
            <Route path="/billing-policy" element={<BillingPolicy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/disclaimer" element={<Disclaimer />} />
            <Route path="/dashboard" element={<Navigate to="/wallet" replace />} />
            <Route path="/success" element={<SubscriptionSuccess />} />
            <Route path="/subscription/success" element={<SubscriptionSuccess />} />
            <Route
              path="/transfer"
              element={
                <ProtectedRoute>
                  <TransferSubscription />
                </ProtectedRoute>
              }
            />
            <Route path="/recover" element={<RecoverSubscription />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/ceo/providers" element={<ProviderHealth />} />
            <Route path="/admin/qa" element={<QaDashboard />} />
            <Route path="/admin/qa/scenario/:scenarioId" element={<QaScenarioDetail />} />
            <Route path="/intel/demo" element={<IntelDemo />} />
            <Route path="/intel/:chain/:token" element={<TokenIntel />} />
            <Route path="/snap/intel/:chainId/:contract" element={<SnapDeepIntelPage />} />
            <Route path="/search" element={<WebSearch />} />
            <Route path="/search/:chain/:token" element={<WebSearch />} />
            <Route path="/wallet" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
            <Route path="/account/security" element={<Navigate to="/search" replace />} />
            <Route
              path="/subscription/cancel"
              element={
                <ProtectedRoute>
                  <SubscriptionCancel />
                </ProtectedRoute>
              }
            />
          </Routes>
        )}
      </main>
      <Footer />
    </BrowserRouter>
  );
}

export function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}
